import {AbstractRepository} from "../repository";

export interface Broadcast {
  id: number
  subject: string
  message: string
  status: "draft" | "published"
  target_team_id: number
  target_type: "clients" | "employees"
  limit_filter: LimitFilter
  is_unread: boolean | null
  updated_at: string
  cover_image_url: string | null
  attachment_path: string | null
  attachment_name: string | null
  attachment_url: string | null
}

export interface BroadcastDetails {
  target_user_subs: string[]
  published_user_subs: string[]
  unpublished_user_subs: string[]
}

export const limitFilterOptions = {
  'all': 'Alle Medewerkers',
  'kantoor-medewerker': 'Alleen Kantoormedewerkers',
  'coordinator': 'Alleen Coordinatoren'
}

export type LimitFilter = keyof typeof limitFilterOptions;


export default class BroadcastRepository extends AbstractRepository {

  /**
   * Fetch all broadcasts
   */
  public async browse(): Promise<Array<Broadcast>> {
    return this.request<{ data: Broadcast[] }>('GET', 'broadcasts')
      .then(this.unpackDataCollectionResponse)
  }

  /**
   * Get broadcast details
   */
  public async read(id: number): Promise<Broadcast&BroadcastDetails> {
    return this.request<{ data: Broadcast&BroadcastDetails }>('GET', `broadcasts/${id}`)
      .then(json => json.data)
  }

  /**
   * Get broadcasts published to the user
   */
  public async browseUserBroadcasts(): Promise<Array<Broadcast>> {
    return this.request<{ data: Broadcast[] }>('GET', 'my_broadcasts')
      .then(this.unpackDataCollectionResponse)
  }

  /**
   * Get broadcasts published to the user
   */
  public async readUserBroadcast(id: number): Promise<Broadcast> {
    return this.request<{ data: Broadcast }>('GET', `my_broadcasts/${id}`)
      .then(json => json.data)
  }


  /**
   * Create a new broadcast
   */
  public async create(subject: string, message: string, cover_image: File | null, target_type: 'employees' | 'clients', targetId: number, attachment: File | null, limitFilter: string): Promise<{
    id: number
  }> {
    const body = new FormData();
    body.set('subject', subject)
    body.set('message', message)
    body.set('target_type', target_type)
    body.set('target_team_id', targetId.toString())
    body.append('limit_filter', limitFilter)
    if (cover_image) {
      body.append('cover_image', cover_image)
    }
    if (attachment) {
      body.append('attachment', attachment)
    }

    const response = await this.rawRequest('POST', `broadcasts`, body)
    const json = await response.json()
    return json.data
  }

  public async update(broadcastId: number, broadcast: Partial<Broadcast>): Promise<void> {
    return this.request<{ data: [] }>('PUT', `broadcasts/${broadcastId}`, broadcast)
      .then(() => undefined)
  }

  public async updateAttachment(broadcastId: number, attatchment: File): Promise<void> {
    return this.request<{
      data: []
    }>('POST', `broadcasts/${broadcastId}/upload-attachment`, attatchment, {fileFormName: "attachment"})
      .then(() => undefined)
  }
  public async updateCoverImage(broadcastId: number, coverImage: File): Promise<void> {
    return this.request<{
      data: []
    }>('POST', `broadcasts/${broadcastId}/upload-cover-image`, coverImage, {fileFormName: "cover_image"})
      .then(() => undefined)
  }

  public async getSendQuote(broadcastId: number): Promise<number> {
    return this.request<{ data: { recipients: number } }>('GET', `broadcasts/${broadcastId}/send-quote`)
      .then((response) => response.data.recipients)
  }

  public async sendBroadcast(broadcastId: number): Promise<void> {
    return this.request<{}>('POST', `broadcasts/${broadcastId}/send`)
      .then(() => undefined)
  }

  public async delete(broadcastId: number): Promise<void> {
    await this.request<{}>('DELETE', `broadcasts/${broadcastId}`)
  }
}
